import Sortable from "sortablejs";
import { showToast } from "./toast.js";

const BOARD_ID = "kanban-board";
const BOARD_WRAPPER_ID = "kanban-board-wrapper";
const COLUMN_SELECTOR = "[data-stage]";
const COUNT_SELECTOR = "[data-column-count]";

// updateColumnCount mirrors the Go liveCount: the header counts live
// work, and the losses parked in the column are tallied separately. A
// plain children.length would overwrite "4 · 1 lost" with "5" on the
// first drag. The lost tally needs no refresh — a lost card cannot be
// dragged, so its column never changes.
function updateColumnCount(column) {
  if (!column) return;
  const wrap = column.closest("[data-column]");
  const countEl = wrap?.querySelector(COUNT_SELECTOR);
  if (!countEl) return;

  const lost = column.querySelectorAll(":scope > [data-lost]").length;
  countEl.textContent = column.children.length - lost;
}

// revertCard returns a dragged card to its origin column + index and
// refreshes both column counts. Used when a move is rejected or fails
// server-side.
function revertCard(card, fromColumn, toColumn, oldIndex) {
  fromColumn.insertBefore(card, fromColumn.children[oldIndex] || null);
  updateColumnCount(fromColumn);
  updateColumnCount(toColumn);
}

// armCloseDealRevert watches an already-open deal modal (opened by an
// optimistic drag onto Closed). The card stays put while the modal is
// open; once the modal leaves the DOM, if the board is still mounted
// the agent dismissed it without creating the deal — so the card
// snaps back. A created deal reloads the page (HX-Redirect) and a
// failed submit replaces it — both tear the board down before this
// fires, so neither strands the card in Closed.
function armCloseDealRevert(card, fromColumn, toColumn, oldIndex) {
  const container = document.getElementById("modal-container");
  if (!container) return;

  const observer = new MutationObserver(() => {
    if (document.getElementById("promote-prospect-modal")) return; // still open
    observer.disconnect();
    if (document.getElementById(BOARD_ID)) {
      revertCard(card, fromColumn, toColumn, oldIndex);
    }
  });
  observer.observe(container, { childList: true });
}

async function persistMove(prospectId, newStage) {
  const body = new URLSearchParams({
    prospect_id: prospectId,
    stage: newStage,
  });

  const res = await fetch("/prospects/move-stage", {
    method: "PATCH",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      "HX-Request": "true",
    },
    body: body.toString(),
  });

  if (res.ok) return;

  throw await moveStageError(res);
}

// moveStageError builds the friendly toast message for a non-2xx
// move-stage response. Status-specific copy keeps the agent informed
// without leaking HTTP details. The technical detail goes to
// console.error for support.
async function moveStageError(res) {
  let message;
  switch (res.status) {
    case 403:
      // loadProspectForWrite returns 403 when the agent lacks the
      // write scope on this prospect (peer's row, team lead without
      // manage-team, etc.). The server body is an HTML modal —
      // discard and surface a static toast.
      message = "You don't have permission to move this prospect.";
      break;
    case 409:
      // Plain-text rule message from the stage-transition guard
      // ("Lead can't move straight to Closed."). Show verbatim.
      message = (await res.text()).trim();
      break;
    case 404:
      message = "This prospect no longer exists.";
      break;
    default:
      message = "Couldn't move prospect. Please try again.";
  }

  console.error("kanban move-stage failed", { status: res.status });
  const err = new Error(`move-stage status=${res.status}`);
  err.userMessage = message;
  err.status = res.status;

  return err;
}

function allowedStagesFor(card) {
  const raw = card.dataset.allowedStages || "";
  if (!raw) return null;

  return new Set(raw.split(","));
}

function markInvalidColumns(board, allowed) {
  if (!allowed) return;
  board.querySelectorAll("[data-stage]").forEach((col) => {
    if (!allowed.has(col.dataset.stage)) {
      col.classList.add("kanban-dropzone-invalid");
    }
  });
}

function clearInvalidColumns(board) {
  board.querySelectorAll(".kanban-dropzone-invalid").forEach((col) => {
    col.classList.remove("kanban-dropzone-invalid");
  });
}

// Pixels the press must travel before it counts as an attempted drag
// rather than a tap. Answering a drag now also swallows the click that
// follows it, so a hand that drifts while clicking would lose the
// navigation it meant — worth a wider bar than the 5px a drag needs.
// Still far short of the ~8px pan slop at which a browser cancels the
// pointer, which is what keeps a scroll silent.
const DRAG_INTENT_PX = 8;

// Milliseconds to wait for the click that follows a release before
// giving up on suppressing it.
const CLICK_AFTER_RELEASE_MS = 400;

// swallowTheClickAfterDrag keeps a refused drag on the board. The card
// wraps its body in a link, and a release fires a click on it whatever
// distance the pointer covered — so without this the page navigates and
// takes the toast with it, which is every word the refusal had to say.
// A tap never reaches here, so it still opens the prospect.
//
// Bound to the card rather than the document: a release anywhere else
// yields a click whose target sits above this anchor, which navigates
// nowhere and needs no swallowing. Nothing else on the page can be
// caught by it.
function swallowTheClickAfterDrag(card) {
  const done = new AbortController();

  card.addEventListener(
    "click",
    (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
    },
    { capture: true, once: true, signal: done.signal },
  );
  // A gesture that ends without a click at all must not leave this
  // armed for the next one.
  setTimeout(() => done.abort(), CLICK_AFTER_RELEASE_MS);
}

// pressOf reads where a press landed and which pointer made it.
// Sortable hands the filter whatever it bound, and that is not always a
// PointerEvent: it falls back to mousedown + touchstart on the Safari
// builds it excludes, which an iPad takes because iPadOS sends the
// desktop user agent. A TouchEvent carries no clientX of its own, so
// reading one off it yields NaN — and NaN fails every threshold test,
// which would toast on the first twitch of a plain tap.
function pressOf(evt) {
  const touch = evt.changedTouches?.[0];
  if (touch) {
    return {
      x: touch.clientX,
      y: touch.clientY,
      id: touch.identifier,
      touch: true,
    };
  }

  return { x: evt.clientX, y: evt.clientY, id: evt.pointerId, touch: false };
}

// noticeOnDragIntent waits to see what the press becomes. Sortable's
// filter runs on the press itself, so answering there scolds an agent
// who only tapped the card to open it — and the detail page is the one
// route to the Reopen button the refusal names.
//
// It answers on release, the way the invalid-column refusal does, and
// stays silent when the gesture is cancelled. That is the difference
// between a drag and a scroll: the browser claims a pan once it passes
// its own slop, which is wider than any threshold worth using, and
// cancelling the pointer is how it says so.
//
// Every listener matches on the pointer that armed it. They sit on the
// document to catch a gesture that leaves the card, so without the
// match one finger's movement answers for a card another finger is
// resting on.
//
// The sentence comes off the card, where the server wrote it. It lives
// in internal/prospects (RefusalWon, RefusalLostMove) so the board, the
// detail card, and the API all say the same thing; a copy here would
// drift.
function noticeOnDragIntent(card, press) {
  if (!Number.isFinite(press.x) || !Number.isFinite(press.y)) return;

  const settled = new AbortController();
  const untilSettled = { signal: settled.signal };
  const [moved, released, cancelled] = press.touch
    ? ["touchmove", "touchend", "touchcancel"]
    : ["pointermove", "pointerup", "pointercancel"];
  let travelled = 0;

  // pointIn returns this press's point in an event that carries it, or
  // null when the event belongs to another pointer.
  const pointIn = (evt) => {
    // press.id is undefined when Sortable bound mousedown, because a
    // MouseEvent carries no pointerId. There is nothing to match on, so
    // take the event: one mouse means one pointer anyway.
    if (!press.touch) {
      return press.id === undefined || evt.pointerId === press.id ? evt : null;
    }

    return (
      Array.from(evt.changedTouches).find((t) => t.identifier === press.id) ||
      null
    );
  };

  document.addEventListener(
    moved,
    (evt) => {
      const point = pointIn(evt);
      if (!point) return;
      travelled = Math.max(
        travelled,
        Math.hypot(point.clientX - press.x, point.clientY - press.y),
      );
    },
    untilSettled,
  );
  document.addEventListener(
    released,
    (evt) => {
      if (!pointIn(evt)) return;
      settled.abort();
      if (travelled >= DRAG_INTENT_PX) {
        swallowTheClickAfterDrag(card);
        showToast(card.dataset.locked, "error");
      }
    },
    untilSettled,
  );
  document.addEventListener(
    cancelled,
    (evt) => {
      if (pointIn(evt)) settled.abort();
    },
    untilSettled,
  );
}

// Stage labels for the bounce-back toast. JS cannot call the Go
// prospectview.StageLabel, and these change rarely enough that a
// 5-entry mirror is cheaper than another round-trip.
const STAGE_LABELS = {
  lead: "Lead",
  contacted: "Contacted",
  presentation_sent: "Presentation Sent",
  meeting_booked: "Meeting Booked",
  closed: "Closed",
};

function stageLabel(stage) {
  return STAGE_LABELS[stage] || stage;
}

// One board is live at a time. A filter swap brings a new one, so the
// old board's Sortable instances are destroyed rather than left holding
// its detached columns.
let liveBoard = null;
let liveSortables = [];

// A board read that overlaps a move may predate it, and swapping it in
// would put the moved card back in its old column. Such a read is
// stale: drop it, and re-run the filters once every move settles.
let moving = 0;
let refilterOnSettle = false;
const boardReads = new Set();

const isBoardRequest = (evt) => evt.detail.target?.id === BOARD_WRAPPER_ID;

document.addEventListener("htmx:beforeRequest", (evt) => {
  if (!isBoardRequest(evt)) return;
  evt.detail.xhr.staleBoard = moving > 0;
  boardReads.add(evt.detail.xhr);
});

document.addEventListener("htmx:afterRequest", (evt) => {
  boardReads.delete(evt.detail.xhr);
});

document.addEventListener("htmx:beforeSwap", (evt) => {
  if (!isBoardRequest(evt) || !evt.detail.xhr.staleBoard) return;
  evt.detail.shouldSwap = false;
  refilterOnSettle = true;
  if (moving === 0) settleMove();
});

function startMove() {
  moving++;
  boardReads.forEach((xhr) => {
    xhr.staleBoard = true;
  });
}

function settleMove() {
  moving = Math.max(0, moving - 1);
  if (moving > 0 || !refilterOnSettle) return;

  refilterOnSettle = false;
  const form = document.getElementById("prospect-filters");
  if (form) window.htmx2.trigger(form, "refilter");
}

// Tier, persona, and scored read the score floored by stage, so a move
// can change whether the card still matches them.
function filtersReadStage() {
  const form = document.getElementById("prospect-filters");

  return ["tier", "persona", "scored"].some(
    (name) => form?.elements.namedItem(name)?.value,
  );
}

export function initKanban(root = document) {
  const scope =
    root instanceof Document ? root : root.ownerDocument || document;
  const board = scope.getElementById(BOARD_ID);
  if (!board || board === liveBoard) return;

  liveSortables.forEach((sortable) => sortable.destroy());
  liveBoard = board;

  const columns = board.querySelectorAll(COLUMN_SELECTOR);

  // Cached on dragstart so onMove (fired on every mousemove) is an O(1)
  // hash lookup instead of re-creating the Set per event.
  let dragAllowed = null;
  // Tracks the most recent invalid target the cursor hovered. If the
  // user releases while still over a denied column, we bounce the card
  // back to its origin instead of letting it land in the last *valid*
  // waypoint Sortable settled on (which would silently downgrade the
  // user's intent).
  let pendingInvalidTarget = null;

  liveSortables = Array.from(
    columns,
    (column) =>
      new Sortable(column, {
        group: "kanban-prospects",
        // A prospect an agent recorded as lost keeps the stage the deal
        // died at — that is what the loss report reads — so the card is
        // not draggable at all. Reopening them is the way back.
        //
        // The function form, rather than the "[data-locked]" selector it
        // replaces, because it is handed the pointerdown event — and the
        // filter event Sortable dispatches afterwards carries no
        // coordinates for the drag-intent notice to start from. Every
        // other refusal on this board says why (onMove reverts and
        // toasts); refusing the pick-up silently left the agent dragging
        // a card that would never move, with no way to learn why.
        filter: (evt, card) => {
          if (!card?.hasAttribute("data-locked")) return false;
          noticeOnDragIntent(card, pressOf(evt));

          return true;
        },
        // Sortable prevents the tap by default when a filter matches,
        // which would stop a locked card opening its own detail page —
        // the one route to the Reopen button this refusal names.
        preventOnFilter: false,
        animation: 150,
        easing: "cubic-bezier(0.23, 1, 0.32, 1)",
        ghostClass: "kanban-ghost",
        chosenClass: "kanban-chosen",
        dragClass: "kanban-drag",
        forceFallback: true,
        fallbackClass: "kanban-fallback",
        fallbackOnBody: true,
        fallbackTolerance: 0,
        onStart: (evt) => {
          startMove();
          board.classList.add("kanban-is-dragging");
          dragAllowed = allowedStagesFor(evt.item);
          pendingInvalidTarget = null;
          markInvalidColumns(board, dragAllowed);
        },
        onMove: (evt) => {
          if (!dragAllowed) {
            pendingInvalidTarget = null;
            return true;
          }
          const target = evt.to.dataset.stage;
          if (target && !dragAllowed.has(target)) {
            pendingInvalidTarget = target;
            return false;
          }
          pendingInvalidTarget = null;

          return true;
        },
        onEnd: (evt) => finishDrag(evt).finally(settleMove),
      }),
  );

  async function finishDrag(evt) {
    board.classList.remove("kanban-is-dragging");
    clearInvalidColumns(board);
    const attemptedInvalid = pendingInvalidTarget;
    dragAllowed = null;
    pendingInvalidTarget = null;

    const card = evt.item;
    const prospectId = card.dataset.prospectId;
    const fromColumn = evt.from;
    const toColumn = evt.to;
    const newStage = toColumn.dataset.stage;
    const oldStage = fromColumn.dataset.stage;

    // User released while hovering a denied column. Restore to
    // origin instead of letting the last valid waypoint silently
    // become the new stage.
    if (attemptedInvalid && attemptedInvalid !== oldStage) {
      revertCard(card, fromColumn, toColumn, evt.oldDraggableIndex);
      showToast(
        `${stageLabel(oldStage)} can't move straight to ${stageLabel(attemptedInvalid)}.`,
        "error",
      );
      return;
    }

    updateColumnCount(fromColumn);
    updateColumnCount(toColumn);

    if (!prospectId || !newStage || oldStage === newStage) return;

    // Closing a deal goes through the deal modal — optimistically
    // leave the card in Closed and open it; armCloseDealRevert
    // snaps the card back if the agent dismisses without closing.
    if (newStage === "closed") {
      const oldIndex = evt.oldDraggableIndex;
      window.htmx2
        .ajax("GET", `/prospects/${prospectId}/promote`, "#modal-container")
        .then(() => {
          if (document.getElementById("promote-prospect-modal")) {
            armCloseDealRevert(card, fromColumn, toColumn, oldIndex);
          } else {
            // The modal never rendered (the fetch failed) — don't
            // strand the card in Closed.
            revertCard(card, fromColumn, toColumn, oldIndex);
          }
        })
        .catch(() => revertCard(card, fromColumn, toColumn, oldIndex));
      return;
    }

    try {
      card.classList.add("kanban-card-syncing");
      await persistMove(prospectId, newStage);
      if (filtersReadStage()) refilterOnSettle = true;
      card.classList.remove("kanban-card-syncing");
      card.classList.add("kanban-card-landed");
      setTimeout(() => card.classList.remove("kanban-card-landed"), 600);
    } catch (err) {
      // Rollback: return card to its original column and position.
      revertCard(card, fromColumn, toColumn, evt.oldDraggableIndex);
      card.classList.remove("kanban-card-syncing");
      if (!err.userMessage) {
        // Network failure or unexpected throw — log the raw detail
        // for support, surface generic copy.
        console.error("kanban move-stage exception", err);
      }
      showToast(
        err.userMessage || "Couldn't move prospect. Please try again.",
        "error",
      );
    }
  }
}
